import React, { useEffect, useState } from "react";
import Header from "../Components/header";
import { useParams, Link, Navigate, useLocation } from "react-router-dom";
import { GraphQLClient } from "graphql-request";
import Footer from "../Components/footer";
import NotFound from "./notFound";
import { RichText } from "@graphcms/rich-text-react-renderer";
import { addToCart } from "./api";
import Skeleton from "react-loading-skeleton";
import { Helmet } from "react-helmet";
import "react-loading-skeleton/dist/skeleton.css";

const API_URL = process.env.REACT_APP_API_URL;

const ProductPage = () => {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [isLogged, setIsLogged] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const fetchProduct = async () => {
      const client = new GraphQLClient(
        "https://api-eu-central-1-shared-euc1-02.hygraph.com/v2/clweq2m6a000001w67aqzeh7x/master"
      );
      const query = `query GetProduct($slug: String!) {
          rivistes(where: { slugRivista: $slug }) {
            id
            numeroRivista
            prezzo
            slugRivista
            copertina {
              fileName
              id
              url
            }
            sommario {
              raw
            }
          }
        }`;

      try {
        const data = await client.request(query, { slug });
        if (data.rivistes.length === 0) {
          setError(true);
        } else {
          const productData = data.rivistes[0];
          const img = new Image();
          img.src = productData.copertina.url;
          img.onload = () => {
            setProduct(productData);
            setIsLoading(false);
          };
          img.onerror = () => {
            setError(true);
            setIsLoading(false);
          };
        }
      } catch (error) {
        console.error("Error fetching product:", error);
      }
    };

    fetchProduct();
  }, [slug]);

  const handleAddToCart = async (id_rivista) => {
    try {
      let response = await addToCart(id_rivista, quantity);
      setIsLogged(response);
    } catch (err) {
      console.log("IMPOSSIBILE AGGIUNGERE AL CARRELLO:", err);
    }
  };

  const incrementQuantity = async () => {
    setQuantity((prevQuantity) => prevQuantity + 1);
  };

  const decrementQuantity = async (index) => {
    if (quantity > 1) {
      setQuantity((prevQuantity) => prevQuantity - 1);
    }
  };

  if (error) {
    return <NotFound />;
  }
  if (isLoading) {
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <div className="flex flex-col justify-center items-center flex-grow">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 p-12 w-screen">
            <div
              className="justify-self-center md:justify-self-end p-8 md:mr-10"
              style={{
                minWidth: "350px",
                minHeight: "350px",
              }}
            >
              <Skeleton width="100%" height="100%" />
            </div>
            <div className="text-left md:mr-10 ">
              <Skeleton width="100%" height="10%" />
              <p className="text-xl font-bold text-gray-500 mt-2">
                <Skeleton width="10%" height="10%" />
              </p>
              <Skeleton width="100%" height="50px" className="w-full my-5" />
              {Array.from({ length: 10 }).map((_, index) => (
                <Skeleton key={index}>
                  <li className="mb-6">Loading...</li>
                </Skeleton>
              ))}
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!isLogged) {
    return <Navigate to="/login" state={{ from: location }} />;
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Helmet>
        <title>Modellistica International - {product.numeroRivista}</title>
        <meta
          name="description"
          content="Scopri il mondo dell'aeromodellismo e modellismo con Modellistica International, la rivista dedicata agli appassionati."
        />
        <meta
          name="keywords"
          content="modellistica, aeromodelli, modellistica international, modellismo, abbonamenti modellismo, rivista modellistica abbonamento"
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org/",
            "@type": "Product",
            name: product.numeroRivista,
            image: product.copertina.url,
            description: product.sommario.raw?.children
              ?.map((node) => node.text)
              ?.join(" "),
            sku: product.id,
            offers: {
              "@type": "Offer",
              url: window.location.href,
              priceCurrency: "EUR",
              price: product.prezzo,
              itemCondition: "https://schema.org/NewCondition",
              availability: "https://schema.org/InStock",
            },
          })}
        </script>
      </Helmet>
      <Header />
      <div className=" flex flex-col justify-center items-center">
        <div className="grid cols-1 md:grid-cols-2 gap-8 p-12 w-screen bg-gresen-500">
          <div className="mr-20 justify-self-end">
            <img
              src={product.copertina.url}
              alt={product.copertina.fileName}
              className="w-full h-auto mx-auto max-w-full sm:max-w-sm md:max-w-md lg:max-w-lg"
              style={{
                maxWidth: "400px",
                maxHeight: "500px",
                minHeight: "90px",
                minWidth: "90px",
              }}
            />
          </div>
          <div className="text-left md:mr-10 bg-bluse-500">
            <p className="text-3xl font-bold mb-4 text-gray-700">
              {product.numeroRivista}
            </p>

            <p className="text-xl font-bold text-gray-500 mt-2">
              € {product.prezzo}
            </p>

            <div className="flex items-center space-x-4">
              <p className="text-lg font-semibold text-gray-500">Quantità</p>

              <button
                onClick={() => incrementQuantity()}
                className="flex justify-center items-center transition hover:bg-[#f3f9ff] border border-gray-500 rounded w-2 sm:w-10 h-2 sm:h-10 text font-semibold text-gray-500 cursor-pointer "
                aria-label="Aumenta quantità"
              >
                +
              </button>

              <span className="text-lg font-medium text-gray-700">
                {quantity}
              </span>

              <button
                onClick={() => decrementQuantity()}
                className="flex justify-center items-center transition hover:bg-[#f3f9ff] border border-gray-500 rounded w-8 sm:w-10 h-8 sm:h-10 text font-semibold text-gray-500 cursor-pointer "
                aria-label="Diminuisci quantità"
              >
                -
              </button>
            </div>

            <button
              onClick={() => handleAddToCart(product.id)}
              className="w-[100%] my-5 font-bold border-4 text-red-500 border-red-500 hover:text-white hover:bg-red-500 h-12 transition"
            >
              AGGIUNGI AL CARRELLO
            </button>
            <div className="text-md text-gray-500 mt-2 ">
              <RichText
                content={product.sommario.raw}
                renderers={{
                  li: ({ children }) => (
                    <li className="ml-6 list-disc mb-2">{children}</li>
                  ),
                }}
              />
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ProductPage;
/*      {clientSecret && (
            <Elements options={options} stripe={stripePromise}>
              <CheckoutForm />
            </Elements>
            //<CheckoutForm />
          )}*/
